import React from "react";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// module level: a new object would make react-pdf reload the document
const PDF_OPTIONS = { isEvalSupported: false };
const MAX_PAGE_WIDTH = 800; // px
const STEP = 5; // pt per arrow key; ×4 with Shift

// Placing the visible signature (face runbook F5): the signing process page
// (face and eID) and the share dialog's fixed place. Coordinates are PDF points of the page as
// displayed (its /Rotate applied, crop box), top-left of the box from the
// page's top-left, pages from 1, whole numbers — the same as the eID flow.
//
// props: file (ArrayBuffer of the document), box {width, height} in pt,
// label (text inside the box), image (optional: the facsimile, drawn in the
// box instead of the label), caption (optional lines under it, as the stamp
// will carry them), value {page, x, y} | null, fixed (no moving),
// onChange(value).
export default class PlacementView extends React.Component {
  constructor(props) {
    super(props);
    // react-pdf may hand the buffer to its worker: give it a copy, once
    this.state = {
      file: { data: new Uint8Array(props.file.slice(0)) },
      numPages: 0,
      sizes: [], // index → {w, h} in pt as displayed
      width: 0, // rendered page width, px
    };
    this.rootRef = React.createRef();
    this.pageRefs = {};
    this.drag = null;
  }

  componentDidMount() {
    this.measure();
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this.rootRef.current);
    } else window.addEventListener("resize", this.measure);
  }

  componentWillUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    else window.removeEventListener("resize", this.measure);
  }

  measure = () => {
    const el = this.rootRef.current;
    if (!el) return;
    const width = Math.min(Math.floor(el.clientWidth), MAX_PAGE_WIDTH);
    if (width !== this.state.width) this.setState({ width });
  };

  // size by page index (pages load in any order), rotation applied
  onPageLoad = (index, page) => {
    const vp = page.getViewport({ scale: 1 });
    this.setState((s) => {
      const sizes = s.sizes.slice();
      sizes[index] = { w: vp.width, h: vp.height };
      return { sizes };
    });
  };

  scaleOf = (index) => {
    const size = this.state.sizes[index];
    return size ? this.state.width / size.w : 0;
  };

  // keeps the box inside the page; whole points from 1 (the gateway draws
  // nothing at 0, and a share's placement needs values above 0)
  clamp = (index, x, y) => {
    const size = this.state.sizes[index];
    const { width, height } = this.props.box;
    return {
      page: index + 1,
      x: Math.round(Math.min(Math.max(x, 1), Math.max(size.w - width, 1))),
      y: Math.round(Math.min(Math.max(y, 1), Math.max(size.h - height, 1))),
    };
  };

  // pointer position on a page, in pt
  pointOnPage = (index, e) => {
    const rect = this.pageRefs[index].getBoundingClientRect();
    const scale = this.scaleOf(index);
    return { x: (e.clientX - rect.left) / scale, y: (e.clientY - rect.top) / scale };
  };

  // click or tap on a page: the box centred on the point
  onPagePointerDown = (index, e) => {
    if (this.props.fixed || !this.state.sizes[index] || e.target !== e.currentTarget) return;
    const p = this.pointOnPage(index, e);
    const { width, height } = this.props.box;
    this.props.onChange(this.clamp(index, p.x - width / 2, p.y - height / 2));
  };

  onBoxPointerDown = (index, e) => {
    if (this.props.fixed) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = this.pointOnPage(index, e);
    const v = this.props.value;
    this.drag = { index, dx: p.x - v.x, dy: p.y - v.y };
  };

  onBoxPointerMove = (e) => {
    if (!this.drag) return;
    const { index, dx, dy } = this.drag;
    const p = this.pointOnPage(index, e);
    this.props.onChange(this.clamp(index, p.x - dx, p.y - dy));
  };

  onBoxPointerUp = () => {
    this.drag = null;
  };

  // arrows move, Shift for bigger steps; PageUp/PageDown change the page
  onBoxKeyDown = (e) => {
    const v = this.props.value;
    if (this.props.fixed || !v) return;
    const step = e.shiftKey ? STEP * 4 : STEP;
    let index = v.page - 1;
    let { x, y } = v;
    switch (e.key) {
      case "ArrowLeft": x -= step; break;
      case "ArrowRight": x += step; break;
      case "ArrowUp": y -= step; break;
      case "ArrowDown": y += step; break;
      case "PageUp": index = Math.max(index - 1, 0); break;
      case "PageDown": index = Math.min(index + 1, this.state.numPages - 1); break;
      default: return;
    }
    e.preventDefault();
    if (this.state.sizes[index]) this.props.onChange(this.clamp(index, x, y));
  };

  renderBox(index) {
    const v = this.props.value;
    const scale = this.scaleOf(index);
    if (!v || v.page !== index + 1 || !scale) return null;
    const { width, height } = this.props.box;
    return (
      <div
        className={`placement-box ${this.props.fixed ? "fixed" : ""}`}
        style={{ left: v.x * scale, top: v.y * scale, width: width * scale, height: height * scale }}
        tabIndex={this.props.fixed ? -1 : 0}
        role={this.props.fixed ? undefined : "slider"}
        aria-label={this.props.ariaLabel}
        aria-valuetext={`${v.page}: ${v.x}, ${v.y}`}
        onPointerDown={(e) => this.onBoxPointerDown(index, e)}
        onPointerMove={this.onBoxPointerMove}
        onPointerUp={this.onBoxPointerUp}
        onPointerCancel={this.onBoxPointerUp}
        onKeyDown={this.onBoxKeyDown}
      >
        {this.props.image || this.props.caption ? (
          // the stamp as it will look: the facsimile, and under it its lines
          <span className="placement-stamp">
            {this.props.image && <img src={this.props.image} alt="" draggable={false} />}
            {(this.props.caption || []).map((line) => (
              <span key={line} className="placement-stamp__line" style={{ fontSize: Math.max(5, 4.5 * scale) }}>
                {line}
              </span>
            ))}
          </span>
        ) : (
          <span style={{ fontSize: Math.max(9, 11 * scale) }}>{this.props.label}</span>
        )}
      </div>
    );
  }

  render() {
    const { numPages, width } = this.state;
    return (
      <div className="placement-view" ref={this.rootRef}>
        {width > 0 && (
          <Document
            file={this.state.file}
            options={PDF_OPTIONS}
            onLoadSuccess={({ numPages }) => this.setState({ numPages })}
            loading={<img src="./assets/images/black-loading.svg" className="content-loader" />}
          >
            {Array.from({ length: numPages }, (_, index) => (
              <div
                key={index}
                className="placement-page"
                ref={(el) => (this.pageRefs[index] = el)}
                style={{ width }}
              >
                <Page
                  pageNumber={index + 1}
                  width={width}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  onLoadSuccess={(page) => this.onPageLoad(index, page)}
                />
                <div
                  className={`placement-layer ${this.props.fixed ? "" : "clickable"}`}
                  onPointerDown={(e) => this.onPagePointerDown(index, e)}
                >
                  {this.renderBox(index)}
                </div>
              </div>
            ))}
          </Document>
        )}
      </div>
    );
  }
}
